// src/components/blog/search/SearchContext.tsx

'use client'

import { logClientError } from '~/lib/logger-client'
import { createContext, useContext, useCallback, useState, useEffect, useMemo } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { trackSearch, trackFilter } from '~/lib/gtag'
import type { SearchResult } from '~/lib/blog/types'

interface SearchContextType {
  results: SearchResult
  isLoading: boolean
  currentFilters: {
    q: string
    author: string
    topic: string
    tag: string
    startDate: string
    endDate: string
  }
  handleSearch: (query: string) => Promise<void>
  handleFilterChange: (key: string, value: string | null) => Promise<void>
}

const SearchContext = createContext<SearchContextType | null>(null)

export function useSearch(): SearchContextType {
  const context = useContext(SearchContext)
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider')
  }
  return context
}

export function SearchProvider({ 
  children, 
  initialResults 
}: { 
  children: React.ReactNode
  initialResults: SearchResult 
}): JSX.Element {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [results, setResults] = useState<SearchResult>(initialResults)
  const [isLoading, setIsLoading] = useState(false)

  // Get current filters from URL - properly memoized to prevent dependency issues
  // This ensures the object reference is stable unless actual filter values change
  const currentFilters = useMemo(() => ({
    q: searchParams.get('q') ?? '',
    author: searchParams.get('author') ?? '',
    topic: searchParams.get('topic') ?? '',
    tag: searchParams.get('tag') ?? '',
    startDate: searchParams.get('startDate') ?? '',
    endDate: searchParams.get('endDate') ?? ''
  }), [searchParams])

  // Update results when initialResults change (server-side updates)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional resync of local state when the source prop changes
    setResults(initialResults)
  }, [initialResults])

  // Update URL params without navigation
  const updateSearchParams = useCallback((key: string, value: string | null) => {
    const params = new URLSearchParams(searchParams.toString())
    if (value === null || value === '') {
      params.delete(key)
    } else {
      params.set(key, value)
    }
    return params.toString()
  }, [searchParams])

  // Handle search
  const handleSearch = useCallback(async (query: string) => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams(searchParams.toString())
      if (query) {
        params.set('q', query)
      } else {
        params.delete('q')
      }

      // Update URL first
      router.replace(`/blog/search?${params.toString()}`, { 
        scroll: false 
      })
      
      // Then perform search
      const response = await fetch(`/api/blog/search?${params.toString()}`)
      if (!response.ok) throw new Error('Search failed')
      const data = (await response.json()) as SearchResult
      setResults(data)
      
      // Track search analytics
      if (query) {
        trackSearch({
          searchQuery: query,
          searchType: 'blog',
          resultCount: data.total,
          sourceLocation: 'blog_search_page'
        })
      }
    } catch (err) {
      logClientError(err, { component: 'SearchContext', context: 'Search error' })
      // Error state will be handled by error boundary
    } finally {
      setIsLoading(false)
    }
  }, [router, searchParams])

  // Handle filter changes
  const handleFilterChange = useCallback(async (key: string, value: string | null) => {
    setIsLoading(true)
    try {
      const queryString = updateSearchParams(key, value)

      // Update URL first
      router.replace(`/blog/search?${queryString}`, { 
        scroll: false 
      })
      
      // Then perform search
      const response = await fetch(`/api/blog/search?${queryString}`)
      if (!response.ok) throw new Error('Search failed')
      const data = (await response.json()) as SearchResult
      setResults(data)
      
      // Track filter analytics
      if (value) {
        const allActiveFilters = Object.entries({ ...currentFilters, [key]: value })
          .filter(([_, v]) => v && v.trim() !== '')
          .map(([k, v]) => `${k}:${v}`)
          
        trackFilter({
          contentType: 'blog',
          filterType: key,
          filterValue: value,
          resultCount: data.total,
          activeFilters: allActiveFilters
        })
      }
    } catch (err) {
      logClientError(err, { component: 'SearchContext', context: 'Filter error' })
      // Error state will be handled by error boundary
    } finally {
      setIsLoading(false)
    }
  }, [router, updateSearchParams, currentFilters]) // currentFilters now properly memoized

  // Memoize the context value to prevent unnecessary re-renders of consumers
  // CRITICAL USER BEHAVIOR CONSIDERATION:
  // This memoization is essential for search input behavior. Without it, every keystroke
  // in SearchInput would cause the entire context to re-render, potentially:
  // 1. Interrupting user typing flow due to component re-mounting
  // 2. Causing input focus loss or cursor position reset
  // 3. Triggering unnecessary re-renders of all search components
  // 4. Breaking the controlled input pattern where local state manages typing
  //
  // The SearchInput component uses a controlled pattern where:
  // - Local state (query) handles real-time typing without triggering searches
  // - Only form submission or explicit actions trigger handleSearch
  // - currentFilters.q syncs the input with URL state on page load/navigation
  //
  // This memoization ensures that typing doesn't break this pattern by preventing
  // context value changes that would cause SearchInput to re-mount and lose focus.
  const contextValue = useMemo(() => ({
    results,
    isLoading,
    currentFilters,
    handleSearch,
    handleFilterChange
  }), [results, isLoading, currentFilters, handleSearch, handleFilterChange])

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  )
} 